/**
 * DYC Wishlist / Save-for-Later System
 * localStorage-backed with optional account sync via customer metafields.
 *
 * Public API:  window.DYCWishlist
 */
(function () {
  'use strict';

  if (window.__DYCWishlistInitialized) return;
  window.__DYCWishlistInitialized = true;

  // ── Constants ──────────────────────────────────────────────────────────
  var STORAGE_KEY = 'dyc_saved_items';
  var MAX_ITEMS = 20;
  var SYNC_ENDPOINT = 'https://dyc-ai-search.vercel.app/api/saved-items';
  var EVENT_NAME = 'dyc:wishlist-update';

  // ── Helpers ────────────────────────────────────────────────────────────

  function readItems() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function writeItems(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_) {
      // storage full — silently fail
    }
  }

  function fireEvent(detail) {
    try {
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: detail }));
    } catch (_) {
      // old browser fallback — skip
    }
  }

  function toast(opts) {
    if (window.DYCToast && typeof window.DYCToast.show === 'function') {
      window.DYCToast.show(opts);
    }
  }

  function cartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  }

  function publishCartUpdate() {
    if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {});
    }
  }

  // ── Account sync (fire-and-forget) ────────────────────────────────────

  function syncToAccount(items) {
    var customerId = window.DYCCustomerId;
    var token = window.DYCSyncToken;
    if (!customerId || !token) return;

    try {
      fetch(SYNC_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dyc-token': token,
        },
        body: JSON.stringify({ customerId: customerId, items: items }),
      }).catch(function () {
        // silently ignore sync errors
      });
    } catch (_) {
      // fetch not available or blocked
    }
  }

  // ── Merge account data on load ────────────────────────────────────────

  function mergeAccountData() {
    var el = document.getElementById('dyc-account-saved-items');
    if (!el) return;

    var accountItems;
    try {
      accountItems = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(accountItems)) return;
    } catch (_) {
      return;
    }

    var localItems = readItems();
    var merged = localItems.slice(); // start with local copy
    var localMap = {};

    for (var i = 0; i < localItems.length; i++) {
      localMap[localItems[i].variantId] = localItems[i];
    }

    for (var j = 0; j < accountItems.length; j++) {
      var acctItem = accountItems[j];
      if (!acctItem || !acctItem.variantId) continue;

      var existing = localMap[acctItem.variantId];
      if (!existing) {
        // not in local — add it
        merged.push(acctItem);
      } else if (acctItem.savedAt && existing.savedAt && acctItem.savedAt > existing.savedAt) {
        // account version is newer — replace
        for (var k = 0; k < merged.length; k++) {
          if (merged[k].variantId === acctItem.variantId) {
            merged[k] = acctItem;
            break;
          }
        }
      }
    }

    // Enforce max items — keep newest
    if (merged.length > MAX_ITEMS) {
      merged.sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
      merged = merged.slice(0, MAX_ITEMS);
    }

    writeItems(merged);
  }

  // ── Public API ─────────────────────────────────────────────────────────

  function getSavedItems() {
    return readItems();
  }

  function saveItem(item) {
    if (!item || !item.variantId) return;

    var items = readItems();

    // Check if already saved
    for (var i = 0; i < items.length; i++) {
      if (items[i].variantId === item.variantId) {
        return; // already saved, do nothing
      }
    }

    var entry = {
      productId: item.productId || null,
      variantId: item.variantId,
      handle: item.handle || null,
      title: item.title || '',
      variantTitle: item.variantTitle || '',
      price: item.price || null,
      compareAtPrice: item.compareAtPrice || null,
      image: item.image || null,
      savedAt: Date.now(),
    };

    items.push(entry);

    // Enforce max — remove oldest if over limit
    if (items.length > MAX_ITEMS) {
      items.sort(function (a, b) { return (a.savedAt || 0) - (b.savedAt || 0); });
      items = items.slice(items.length - MAX_ITEMS);
    }

    writeItems(items);
    fireEvent({ action: 'save', item: entry });

    /* Toast is NOT shown here — the caller (cart-drawer heart handler)
       shows a toast with an Undo action instead. Avoids double toast. */

    syncToAccount(readItems());
  }

  function removeItem(variantId) {
    if (!variantId) return;
    variantId = Number(variantId);

    var items = readItems();
    var filtered = [];
    for (var i = 0; i < items.length; i++) {
      if (Number(items[i].variantId) !== variantId) {
        filtered.push(items[i]);
      }
    }

    writeItems(filtered);
    fireEvent({ action: 'remove', variantId: variantId });
    syncToAccount(filtered);
  }

  function isItemSaved(variantId) {
    if (!variantId) return false;
    variantId = Number(variantId);
    var items = readItems();
    for (var i = 0; i < items.length; i++) {
      if (items[i].variantId === variantId) return true;
    }
    return false;
  }

  function moveToCart(variantId) {
    variantId = Number(variantId);
    var items = readItems();
    var item = null;
    for (var i = 0; i < items.length; i++) {
      if (Number(items[i].variantId) === variantId) {
        item = items[i];
        break;
      }
    }

    if (!item) return Promise.resolve(false);

    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: [{ id: Number(item.variantId), quantity: 1 }] }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (data && data.status) throw new Error(data.description || 'Add failed');
        try {
          removeItem(variantId);
          toast({
            type: 'cart',
            title: 'Moved to cart',
            desc: item.title + (item.variantTitle ? ' \u00b7 ' + item.variantTitle : ''),
          });
          publishCartUpdate();
        } catch(e) { /* ignore subscriber/listener errors */ }
        return true;
      })
      .catch(function (err) {
        // Only show error toast if the fetch actually failed
        if (err && err.message && err.message.indexOf('Add failed') !== -1) {
          toast({
            type: 'warning',
            title: 'Could not add to cart',
            desc: 'This item is no longer available',
          });
          removeItem(variantId);
        }
        return false;
      });
  }

  function moveAllToCart() {
    var items = readItems();
    if (items.length === 0) return Promise.resolve();

    var addedCount = 0;
    var queue = items.slice(); // shallow copy

    function processNext() {
      if (queue.length === 0) {
        if (addedCount > 0) {
          toast({
            type: 'success',
            title: 'Added ' + addedCount + ' item' + (addedCount !== 1 ? 's' : '') + ' to cart',
            desc: 'All saved items moved to your cart',
          });
          publishCartUpdate();
        }
        return Promise.resolve();
      }

      var item = queue.shift();
      return fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: Number(item.variantId), quantity: 1 }] }),
      })
        .then(function (res) {
          if (res.ok) {
            addedCount++;
            removeItem(item.variantId);
          } else {
            // item unavailable — just remove from saved
            removeItem(item.variantId);
          }
        })
        .catch(function () {
          removeItem(item.variantId);
        })
        .then(processNext);
    }

    return processNext();
  }

  function getCount() {
    return readItems().length;
  }

  function onUpdate(callback) {
    if (typeof callback !== 'function') return function () {};

    function handler(e) {
      callback(e.detail);
    }

    document.addEventListener(EVENT_NAME, handler);
    return function unsubscribe() {
      document.removeEventListener(EVENT_NAME, handler);
    };
  }

  // ── Initialize ─────────────────────────────────────────────────────────

  // Merge account data when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mergeAccountData);
  } else {
    mergeAccountData();
  }

  // ── Expose public API ──────────────────────────────────────────────────

  window.DYCWishlist = {
    getSavedItems: getSavedItems,
    saveItem: saveItem,
    removeItem: removeItem,
    isItemSaved: isItemSaved,
    moveToCart: moveToCart,
    moveAllToCart: moveAllToCart,
    getCount: getCount,
    onUpdate: onUpdate,
  };
})();
